import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Paper,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    StepContent
} from '@mui/material';
import {
    Upload as UploadIcon,
    Check as CheckIcon,
    Error as ErrorIcon,
    CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

const OperatorUploadModal = ({ open, onClose, onOperatorUploaded }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [jsonData, setJsonData] = useState('');
    const [validationResult, setValidationResult] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    
    const { getAccessTokenSilently } = useAuth0();

    const steps = [
        'Upload JSON File',
        'Validate Structure',
        'Review and Confirm',
        'Upload Complete'
    ];

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file && file.type === 'application/json') {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const content = e.target.result;
                    const parsed = JSON.parse(content);
                    setJsonData(content);
                    validateOperatorData(parsed);
                    setActiveStep(1); // Move to validation step
                } catch (error) {
                    setUploadError('Invalid JSON file. Please check the format and try again.');
                }
            };
            reader.readAsText(file);
        } else {
            setUploadError('Please select a valid JSON file.');
        }
    };

    const validateOperatorData = (data) => {
        const validation = {
            valid: true,
            errors: [],
            warnings: [],
            summary: {}
        };

        // Required fields validation
        const requiredFields = ['name', 'response_model', 'factors', 'items'];
        requiredFields.forEach(field => {
            if (!data[field]) {
                validation.errors.push(`Missing required field: ${field}`);
                validation.valid = false;
            }
        });

        if (!data.type) {
            data.type = 'user_scale'; // Default type if not provided
        }

        // Validate response model
        if (data.response_model) {
            if (!data.response_model.type) {
                validation.errors.push('Response model must have a type');
                validation.valid = false;
            }
            if (!data.response_model.range || !Array.isArray(data.response_model.range) || data.response_model.range.length !== 2) {
                validation.errors.push('Response model must have a valid range array [min, max]');
                validation.valid = false;
            }
        }

        // Validate factors
        if (data.factors && Array.isArray(data.factors)) {
            validation.summary.factorCount = data.factors.length;
            data.factors.forEach((factor, index) => {
                if (!factor.label) {
                    validation.errors.push(`Factor ${index + 1} is missing a label`);
                    validation.valid = false;
                }
                if (!factor.items_included || !Array.isArray(factor.items_included)) {
                    validation.errors.push(`Factor ${index + 1} must have items_included array`);
                    validation.valid = false;
                }
            });
        } else {
            validation.errors.push('Factors must be an array');
            validation.valid = false;
        }

        // Validate items
        if (data.items && typeof data.items === 'object') {
            validation.summary.itemCount = Object.keys(data.items).length;
            Object.entries(data.items).forEach(([id, item]) => {
                if (!item.text) {
                    validation.errors.push(`Item ${id} is missing text`);
                    validation.valid = false;
                }
            });
        } else {
            validation.errors.push('Items must be an object');
            validation.valid = false;
        }

        // Generate summary
        validation.summary.name = data.name;
        validation.summary.type = data.type;
        validation.summary.version = data.version || '1.0';
        validation.summary.authors = data.authors || [];

        setValidationResult(validation);
        setUploadError('');
        
        // Don't auto-advance if there are errors
        if (!validation.valid) {
            setActiveStep(1); // Stay on validation step to show errors
        }
    };

    const handleUpload = async () => {
        setUploading(true);
        setUploadError('');

        try {
            const token = await getAccessTokenSilently();
            const operatorData = JSON.parse(jsonData);
            
            // Add metadata
            operatorData.created_at = new Date().toISOString();
            operatorData.type = operatorData.type || 'user_scale';

            const response = await axios.post(
                `${API_URL}/api/simulations/operators`,
                operatorData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setActiveStep(3);
            if (onOperatorUploaded) {
                onOperatorUploaded(response.data);
            }
        } catch (error) {
            console.error('Upload failed:', error);
            setUploadError(
                error.response?.data?.message || 
                'Failed to upload operator. Please check your data and try again.'
            );
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setActiveStep(0);
        setJsonData('');
        setValidationResult(null);
        setUploadError('');
        setUploading(false);
        onClose();
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Upload a JSON file containing your operator definition. The file should include:
                            name, type, response_model, factors, items, and optionally authors and description.
                        </Typography>
                        
                        <Button
                            variant="text" 
                            size="small"
                            onClick={() => {
                                const link = document.createElement('a');
                                link.href = '/example-operator.json';
                                link.download = 'example-operator.json';
                                link.click();
                            }}
                            sx={{ mb: 2 }}
                        >
                            Download Example JSON Format
                        </Button>
                        
                        <Paper
                            sx={{
                                border: '2px dashed',
                                borderColor: 'grey.300',
                                borderRadius: 2,
                                p: 3,
                                textAlign: 'center',
                                cursor: 'pointer',
                                '&:hover': { borderColor: 'primary.main' }
                            }}
                            onClick={() => document.getElementById('operator-file-input').click()}
                        >
                            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography variant="h6">
                                Click to upload JSON file
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Or drag and drop your file here
                            </Typography>
                        </Paper>
                        
                        <input
                            id="operator-file-input"
                            type="file"
                            accept=".json,application/json"
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                        />
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        {validationResult && (
                            <Box>
                                {validationResult.valid ? (
                                    <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
                                        Validation successful! Your operator is ready to upload.
                                    </Alert>
                                ) : (
                                    <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                                        Validation failed. Please fix the following issues:
                                    </Alert>
                                )}

                                {validationResult.errors.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="error">
                                            Errors:
                                        </Typography>
                                        {validationResult.errors.map((error, index) => (
                                            <Typography key={index} variant="body2" color="error" sx={{ ml: 2 }}>
                                                • {error}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                {validationResult.warnings.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="warning.main">
                                            Warnings:
                                        </Typography>
                                        {validationResult.warnings.map((warning, index) => (
                                            <Typography key={index} variant="body2" color="warning.main" sx={{ ml: 2 }}>
                                                • {warning}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                {validationResult.valid && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            Operator Summary:
                                        </Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="body2">
                                                <strong>Name:</strong> {validationResult.summary.name}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Type:</strong> {validationResult.summary.type}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Version:</strong> {validationResult.summary.version}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Factors:</strong> {validationResult.summary.factorCount}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Items:</strong> {validationResult.summary.itemCount}
                                            </Typography>
                                            {validationResult.summary.authors.length > 0 && (
                                                <Typography variant="body2">
                                                    <strong>Authors:</strong> {validationResult.summary.authors.join(', ')}
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Ready to upload your operator. Click "Upload" to add it to your library.
                        </Typography>
                        
                        {uploading && (
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Uploading operator...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );

            case 3:
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Upload Complete!
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Your operator has been successfully added to the library.
                        </Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CloudUploadIcon sx={{ mr: 2 }} />
                    Upload New Operator
                </Box>
            </DialogTitle>

            <DialogContent>
                {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {uploadError}
                    </Alert>
                )}

                <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((label, index) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                            <StepContent>
                                {renderStepContent(index)}
                                
                                <Box sx={{ mt: 2 }}>
                                    {index === 1 && validationResult?.valid && (
                                        <Button
                                            variant="contained"
                                            onClick={() => setActiveStep(2)}
                                            sx={{ mr: 1 }}
                                        >
                                            Continue
                                        </Button>
                                    )}
                                    
                                    {index === 2 && (
                                        <Button
                                            variant="contained"
                                            onClick={handleUpload}
                                            disabled={uploading}
                                            sx={{ mr: 1 }}
                                        >
                                            Upload
                                        </Button>
                                    )}
                                    
                                    {index > 0 && index < 3 && (
                                        <Button
                                            onClick={() => setActiveStep(index - 1)}
                                            disabled={uploading}
                                        >
                                            Back
                                        </Button>
                                    )}
                                </Box>
                            </StepContent>
                        </Step>
                    ))}
                </Stepper>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>
                    {activeStep === 3 ? 'Close' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OperatorUploadModal;
